// LumaLux Face (PE748) conversion template: set picker updates price/button/gallery/URL, gallery thumbnails, sticky add-to-cart bar.
(() => {
  if (window.__pe748) return;
  window.__pe748 = true;

  const init = (root) => {
    const buy = root.querySelector('[data-pe748-buy]');
    if (!buy || buy.dataset.ready) return;
    buy.dataset.ready = 'true';
    let variants = [];
    try { variants = JSON.parse(buy.querySelector('[data-pe748-variants]').textContent); } catch { /* keep defaults */ }
    const form = buy.querySelector('form');
    const track = buy.querySelector('[data-pe748-track]');
    const dots = [...buy.querySelectorAll('[data-pe748-thumb]')];
    const sticky = buy.querySelector('[data-pe748-sticky]');
    const atc = buy.querySelector('[data-pe748-atc]');

    form?.addEventListener('change', (event) => {
      if (event.target.name !== 'id') return;
      const v = variants.find(x => String(x.id) === event.target.value);
      if (!v) return;
      buy.querySelectorAll('[data-price]').forEach(el => { el.textContent = v.price; });
      buy.querySelectorAll('[data-atc-price]').forEach(el => { el.textContent = v.price; });
      const compare = buy.querySelector('[data-compare]');
      if (compare) { compare.hidden = !v.compare; compare.textContent = v.compare || ''; }
      buy.querySelectorAll('button[type="submit"]').forEach(b => { b.disabled = !v.available; });
      const label = buy.querySelector('[data-sticky-variant]');
      if (label) label.textContent = v.title;
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState(null, '', url);
      if (v.media && track) {
        const item = track.querySelector(`[data-media-id="${v.media}"]`);
        if (item && matchMedia('(max-width: 989px)').matches) track.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
      }
    });

    if (track && dots.length) {
      let frame;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          dots.forEach((d, n) => {
            d.classList.toggle('is-active', n === i);
            n === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current');
          });
          const active = dots[i];
          if (active) { const strip = active.parentElement; strip.scrollTo({ left: active.offsetLeft - strip.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' }); }
        });
      }, { passive: true });
      dots.forEach((d) => d.addEventListener('click', () => {
        const item = track.children[Number(d.dataset.pe748Thumb)];
        if (item) track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }));
    }

    // Payment options: hide rows that render nothing in this market (e.g. no Shop Pay Installments), and the box if empty
    const payments = buy.querySelector('.pe748-payments');
    if (payments) {
      const tidy = () => {
        const rows = [...payments.querySelectorAll('.buybox-payments__credit > div, .buybox-payments__option:not(.buybox-payments__credit)')];
        rows.forEach(row => {
          const host = row.querySelector('shopify-payment-terms');
          const shadowText = host?.shadowRoot?.textContent.trim();
          const copy = row.cloneNode(true); copy.querySelectorAll('style, script, template').forEach(n => n.remove());
          const empty = !copy.textContent.trim() && !shadowText && copy.querySelectorAll('img, svg, iframe').length === 0;
          row.hidden = empty;
        });
        const credit = payments.querySelector('.buybox-payments__credit');
        if (credit) credit.hidden = ![...credit.children].some(c => !c.hidden);
        payments.hidden = !rows.some(r => !r.hidden);
      };
      tidy();
      window.addEventListener('load', () => setTimeout(tidy, 1500));
      new MutationObserver(tidy).observe(payments, { childList: true, subtree: true, characterData: true });
    }

    // Desktop: both columns scroll together; the shorter one sticks once its bottom is in view until the other ends
    const cols = [buy.querySelector('[data-pe748-gallery]'), buy.querySelector('.pe748-info')].filter(Boolean);
    if (cols.length === 2) {
      const place = () => {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 80;
        const offset = header + 24;
        cols.forEach(col => {
          const top = Math.min(offset, window.innerHeight - col.offsetHeight - 24);
          col.style.setProperty('--pe748-stick', top + 'px');
        });
      };
      place();
      window.addEventListener('resize', place, { passive: true });
      if ('ResizeObserver' in window) { const ro = new ResizeObserver(place); cols.forEach(c => ro.observe(c)); }
    }

    // Sticky bar: visible while the main add-to-cart button is out of view (and not above the buy box)
    if (sticky && atc) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < window.innerHeight;
        const below = !entry.isIntersecting && entry.boundingClientRect.top >= window.innerHeight;
        sticky.classList.toggle('is-visible', show || below);
        sticky.setAttribute('aria-hidden', String(!(show || below)));
        sticky.querySelector('button')?.setAttribute('tabindex', show || below ? '0' : '-1');
      }).observe(atc);
    }
  };

  // Lazy autoplay videos, ring/stat reveal, rail arrows, 3D model on approach
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const el = e.target;
      if (el.matches('video')) { if (e.isIntersecting) { el.play().catch(() => {}); } else { el.pause(); } return; }
      if (!e.isIntersecting) return;
      if (el.matches('[data-pe748-reveal]')) el.classList.add('is-in');
      if (el.matches('[data-pe748-model]')) loadModel(el);
      if (!el.matches('video')) io.unobserve(el);
    });
  }, { rootMargin: '200px 0px' }) : null;

  let modelLib;
  const loadModel = (mv) => {
    if (!modelLib && !customElements.get('model-viewer')) {
      modelLib = document.createElement('script');
      modelLib.type = 'module';
      modelLib.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
      document.head.appendChild(modelLib);
    }
    if (mv.dataset.src) { mv.setAttribute('src', mv.dataset.src); mv.removeAttribute('data-src'); }
  };

  const enhance = (root) => {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('.pe748 video[data-pe748-autoplay]').forEach(v => { if (!reduce && io) io.observe(v); });
    root.querySelectorAll('.pe748 [data-pe748-reveal]').forEach(el => { io ? io.observe(el) : el.classList.add('is-in'); });
    root.querySelectorAll('.pe748 [data-pe748-model]').forEach(el => { io ? io.observe(el) : loadModel(el); });
    root.querySelectorAll('.pe748 [data-pe748-rail]').forEach(rail => {
      const nav = rail.parentElement.querySelector('.pe748-rail__nav');
      nav?.querySelectorAll('[data-dir]').forEach(btn => btn.addEventListener('click', () => {
        const card = rail.firstElementChild;
        rail.scrollBy({ left: Number(btn.dataset.dir) * (card ? card.getBoundingClientRect().width + 20 : rail.clientWidth), behavior: 'smooth' });
      }));
    });
  };

  // FAQ category tabs (without JS every group stays visible)
  const tabs = (root) => {
    root.querySelectorAll('.pe748 [data-pe748-tabs]').forEach(box => {
      const list = [...box.querySelectorAll('[role="tab"]')];
      if (!list.length || box.dataset.ready) return;
      box.dataset.ready = 'true';
      const select = (tab, focus) => list.forEach(t => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = box.querySelector('#' + CSS.escape(t.getAttribute('aria-controls')));
        if (panel) panel.hidden = !on;
        if (on && focus) t.focus();
      });
      list.forEach((t, i) => {
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', (e) => {
          const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (step) { e.preventDefault(); select(list[(i + step + list.length) % list.length], true); }
        });
      });
      select(list.find(t => t.getAttribute('aria-selected') === 'true') || list[0]);
    });
  };

  const run = () => { init(document); enhance(document); tabs(document); };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  document.addEventListener('shopify:section:load', (e) => { init(e.target); enhance(e.target); tabs(e.target); });
})();
